import {
  Box,
  Chip,
  FormControl,
  MenuItem,
  Select,
  Typography,
} from "@mui/material";
import { AlertContext } from "contexts/Alert";
import { CheckoutContext } from "contexts/Checkout";
import { OrganizationUserContext } from "contexts/Organization";
import { SplashPage, SubscriptionContext } from "contexts/Subscription";
import { useContext, useEffect, useState } from "react";
import { AddOnItemName, BannerCouponName } from "schemas/dashboard";
import { Plans } from "schemas/payments";

import { useCheckout } from "components/StripeCheckout/useCheckout";

import SeeExample from "../SeeExample";

interface Props {
  displayName: string;
}

const PlanDetailsSettings = ({ displayName }: Props) => {
  const { currentOrg } = useContext(OrganizationUserContext);
  const { addOns } = useContext(SubscriptionContext);
  const { setAlert } = useContext(AlertContext);
  const { plan, splashPageSource, invoice } = useContext(CheckoutContext);
  const { canPurchaseMoreEmailVolume } = useCheckout();
  const { subscription } = useContext(SubscriptionContext);

  const canAdjustQuantity =
    plan && plan in addOns && addOns[plan]?.can_purchase_multiple;
  const [quantity, setQuantity] = useState<number | undefined>(undefined);
  const { applyAddOnItem } = useCheckout();

  useEffect(() => {
    if (!quantity && currentOrg?.id && plan && canAdjustQuantity) {
      applyAddOnItem({
        name: plan as AddOnItemName,
        quantity: 1,
      });
      setQuantity(1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentOrg?.id, plan, canAdjustQuantity]);

  const isVaMoneyBack =
    splashPageSource === SplashPage.va_money_back ||
    (subscription?.bannerPromoCode?.couponName ===
      BannerCouponName.money_back_guarantee &&
      plan === Plans.VA_YEARLY);
  return (
    <>
      <Typography
        variant="h2"
        component="div"
        sx={{
          color: "black",
          fontSize: 40,
          fontWeight: 500,
          mb: 1,
        }}
      >
        {displayName}{" "}
        {plan === AddOnItemName.partnership_data && <SeeExample />}
        {isVaMoneyBack && (
          <Chip label="Money-back guaranteed 💸" color="primary" size="small" />
        )}
      </Typography>

      {isVaMoneyBack && (
        <Typography sx={{ color: "green.main", opacity: 0.8 }}>
          <Box
            component="i"
            className="fa-solid fa-circle-check"
            sx={{ mr: 1 }}
          />
          <em>
            If you don't earn more than you pay in your first year, we'll refund
            you. No fuss.
          </em>
        </Typography>
      )}

      {canAdjustQuantity && invoice?.id && (
        <>
          <Typography gutterBottom>
            How many do you want to purchase?
          </Typography>
          <FormControl sx={{ minWidth: 120, mb: 2 }}>
            <Select
              sx={{
                backgroundColor: "white",
                borderRadius: 4,
                minWidth: 80,
              }}
              value={quantity || 1}
              label=""
              onChange={async (event) => {
                if (
                  plan === AddOnItemName.email_volume_monthly ||
                  plan === AddOnItemName.email_volume_yearly
                ) {
                  const canPurchaseMore = canPurchaseMoreEmailVolume(
                    event.target.value as number,
                  );
                  if (!canPurchaseMore) {
                    setAlert(
                      `You are only allowed up to ${addOns[plan]?.max_quantity} packages (including your current subscriptions)`,
                      "warning",
                    );
                    return;
                  }
                }
                setQuantity(event.target.value as number);
                await applyAddOnItem({
                  name: plan as AddOnItemName,
                  quantity: event.target.value as number,
                });
              }}
            >
              {Array.from(
                { length: addOns[plan]?.max_quantity },
                (_, i) => i + 1,
              ).map((num) => (
                <MenuItem key={num} value={num}>
                  {num}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </>
      )}
    </>
  );
};

export default PlanDetailsSettings;
